import {
  createAnalyticsLifecycleState,
  syncAnalyticsGroup,
  syncAnalyticsIdentity,
} from "@frontend-shared/product-analytics";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "@/hooks/use-auth";
import {
  capturePageview,
  initProductAnalytics,
  productAnalytics,
} from "@/lib/product-analytics";

// NOTE: Owns the PostHog lifecycle for the web app (epic NSAI-2431 /
// NSAI-4614): boot the SDK, identify the person, attach them to their project
// group, emit an SPA pageview per navigation, and reset on logout.
//
// Identity and grouping are the shared rules from `@frontend-shared` — mobile
// runs the very same functions, so the two platforms cannot drift on who gets
// identified or when a reset happens. What stays here is genuinely
// web-specific: react-router as the navigation source, and the web client.
//
// Mounted inside `AuthProvider` (needs the user + project) and inside
// `BrowserRouter` (needs `useLocation`). Renders nothing — it is a lifecycle
// component, not UI.
export const ProductAnalyticsProvider = (): null => {
  const { user, currentProjectUuid, projects } = useAuth();
  const location = useLocation();

  // NOTE: A ref, not state — what we last told PostHog is bookkeeping and must
  // never trigger a re-render.
  const lifecycleStateRef = useRef(createAnalyticsLifecycleState());

  useEffect(() => {
    void initProductAnalytics();
  }, []);

  useEffect(() => {
    syncAnalyticsIdentity({
      state: lifecycleStateRef.current,
      user,
      client: productAnalytics,
    });
  }, [user]);

  useEffect(() => {
    syncAnalyticsGroup({
      state: lifecycleStateRef.current,
      currentProjectUuid: currentProjectUuid ?? null,
      projects,
      isAuthenticated: Boolean(user),
      client: productAnalytics,
    });
  }, [user, currentProjectUuid, projects]);

  // NOTE: `capture_pageview` is off in the SDK config because its default only
  // fires on a full page load — in a react-router SPA that means one pageview
  // per session and no funnel can see a navigation. This fires one per route
  // change instead.
  useEffect(() => {
    capturePageview({ path: `${location.pathname}${location.search}` });
  }, [location.pathname, location.search]);

  return null;
};

export default ProductAnalyticsProvider;
